/**
 * 結帳 OTP 的判斷邏輯——唯一來源。
 *
 * 為什麼要獨立成模組：這組判斷原本散在 cart.tsx（要不要預寄簡訊）與 confirm.tsx
 * （要不要顯示驗證區塊／要不要擋送出）各寫一份，兩邊用了不同條件而走鐘：
 * 顯示端已改用後端 phone_verified，預寄端卻還留著舊的
 * `entrySource === "qr" && entryCylinderScan && !otpVerified`——
 * entrySource 永不清除、resetCheckout 又把 otpVerified 清成 false，
 * 結果每筆訂單按「前往結帳」都寄一封結算頁根本不會顯示的簡訊（真簡訊要錢）。
 *
 * 一律從這裡取判斷，新增呼叫端也不會再各寫一套。
 */

export interface CheckoutOtpState {
  /** 後端 phone_verified === false 時為 true（記於 customer.phone_verified_at）。 */
  needCheckoutOtp: boolean;
  /** 本次結帳是否已驗證通過。 */
  otpVerified: boolean;
}

/**
 * 是否顯示簡訊驗證區塊（驗過仍顯示，由區塊自己切換成「✓ 驗證成功」）。
 *
 * 也看 otpVerified：本次在結帳頁驗證成功後，後端會把 phone_verified 改為 true，
 * 重新整理時 Gate 取回新值 → needCheckoutOtp 變 false。若只看它，整張卡（含「✓ 驗證成功」）
 * 會突然消失，使用者以為驗證被清掉了。驗過就一直顯示成功狀態到本次結帳結束。
 */
export const shouldShowOtpCard = (s: CheckoutOtpState): boolean =>
  s.needCheckoutOtp || s.otpVerified;

/** 是否擋住「確認送出」。 */
export const shouldBlockSubmit = (s: CheckoutOtpState): boolean =>
  s.needCheckoutOtp && !s.otpVerified;

/**
 * 按「前往結帳」時是否預寄簡訊。
 * 必須與 shouldBlockSubmit 同條件：只有「結算頁真的會要求驗證」的人才值得先寄，
 * 否則就是寄一封不會被使用的付費簡訊。
 */
export const shouldPrefetchOtp = (s: CheckoutOtpState): boolean => shouldBlockSubmit(s);
